import {
  getParsedDateAsISO,
  safeParseDate,
  getCountryCodeByPhoneNumber,
} from '@lili/utils/formatting';
import { OnboardingFormFields } from '@Types/index.ts';
import { useBoundStore } from '@Store/index.ts';
import { IdentityFormTypes } from '@Components/Identity/IdentityFormTypeSwitcher';
import { DEFAULT_DATE_FORMAT } from '@Consts/constants';
import { setGA4UserProperties } from '@Services/analytics/analytics.service';
import type { IIdentityForm, IIdentityUsForm } from '@Lib/Identity/types';

export const syncSliceWithUsFormData = (props: IIdentityUsForm) => {
  const { setCustomerData, getCustomerData } = useBoundStore.getState();

  const customerDataToUpdate = getCustomerData();
  setCustomerData({
    ...customerDataToUpdate,
    customerData: {
      ...customerDataToUpdate.customerData,
      ssn: props[OnboardingFormFields.SSN],
      birthDate: getParsedDateAsISO(props[OnboardingFormFields.DATE_OF_BIRTH]),
    },
  });
  setGA4UserProperties();
};

export const syncSliceWithNonUsFormData = (props: IIdentityForm) => {
  const { setCustomerData, getCustomerData } = useBoundStore.getState();

  const customerDataToUpdate = getCustomerData();
  setCustomerData({
    ...customerDataToUpdate,
    customerData: {
      ...customerDataToUpdate.customerData,
      idGovNumber: props[OnboardingFormFields.NATIONAL_ID_NUMBER],
      idCountry: props[OnboardingFormFields.NATIONALITY],
      birthDate: getParsedDateAsISO(props[OnboardingFormFields.DATE_OF_BIRTH]),
      idNumber: props[OnboardingFormFields.PASSPORT_NUMBER],
    },
  });
  setGA4UserProperties();
};

export const getIdentityDefaultValues = (formType: IdentityFormTypes) => {
  const {
    customerData: { ssn, birthDate, idNumber, idCountry, idGovNumber },
  } = useBoundStore.getState().customer;
  const phoneNumber = useBoundStore.getState().customer.customerData.phoneNumber;
  const CountryFromPhone = phoneNumber ? getCountryCodeByPhoneNumber(phoneNumber) : '';

  let country = idCountry ?? '';
  if (CountryFromPhone && CountryFromPhone !== 'US') {
    country = CountryFromPhone;
  }

  const defaultValues: IIdentityForm = {
    [OnboardingFormFields.SSN]: ssn || '',
    [OnboardingFormFields.NATIONALITY]: country,
    [OnboardingFormFields.DATE_OF_BIRTH]: birthDate
      ? safeParseDate(birthDate, DEFAULT_DATE_FORMAT)
      : '',
    [OnboardingFormFields.PASSPORT_NUMBER]: idNumber || '',
    [OnboardingFormFields.NATIONAL_ID_NUMBER]: idGovNumber || '',
    [OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_A]: false,
    [OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_B]: false,
    formType: formType,
  };

  return defaultValues;
};

export const clearCustomerDataOnSubmit = (formType: IdentityFormTypes) => {
  const getCustomerData = useBoundStore.getState().getCustomerData;
  const setCustomerData = useBoundStore.getState().setCustomerData;

  if (formType === IdentityFormTypes.us) {
    const customer = { ...getCustomerData() };
    if (customer?.customerData?.idCountry) {
      customer.customerData.businessAddressData.sameAsPrimary = false;
    }
    /** Non US identity fields */
    customer.customerData.idCountry = '';
    customer.customerData.idGovNumber = '';
    customer.customerData.idNumber = '';
    customer.customerData.idExpirationDate = '';
    customer.customerData.idType = '';

    if (
      customer?.customerData?.customerAddressData?.country &&
      customer.customerData.customerAddressData.country !== 'US'
    ) {
      /** Home address fields */
      customer.customerData.customerAddressData.line1 = '';
      customer.customerData.customerAddressData.line2 = '';
      customer.customerData.customerAddressData.city = '';
      customer.customerData.customerAddressData.state = '';
      customer.customerData.customerAddressData.country = '';
      customer.customerData.customerAddressData.postalCode = '';
    }

    if (
      customer?.customerData?.businessAddressData?.country &&
      customer.customerData.businessAddressData.country !== 'US'
    ) {
      /** Business address fields */
      customer.customerData.businessAddressData.line1 = '';
      customer.customerData.businessAddressData.line2 = '';
      customer.customerData.businessAddressData.city = '';
      customer.customerData.businessAddressData.state = '';
      customer.customerData.businessAddressData.country = '';
      customer.customerData.businessAddressData.postalCode = '';
      customer.customerData.businessAddressData.sameAsPrimary = false;
    }

    /** Only for NONUS - Clear all Mailing address fields */
    if (customer.customerData.mailingAddressData) {
      customer.customerData.mailingAddressData.line1 = '';
      customer.customerData.mailingAddressData.line2 = '';
      customer.customerData.mailingAddressData.city = '';
      customer.customerData.mailingAddressData.state = '';
      customer.customerData.mailingAddressData.country = 'US';
      customer.customerData.mailingAddressData.postalCode = '';
    }

    if (customer.customerData.customerBusinessData.businessUbos?.uboResponseList?.length) {
      customer.customerData.customerBusinessData.businessUbos.uboResponseList = [];
    }

    setCustomerData(customer);
  } else if (formType === IdentityFormTypes.nonUs) {
    const customer = { ...getCustomerData() };
    if (customer?.customerData?.ssn) {
      customer.customerData.businessAddressData.sameAsPrimary = false;
    }
    customer.customerData.ssn = '';

    setCustomerData(customer);
  }
};
